import React from 'react';

/**
 * Higher-order component to wrap i18n loading and provide safe fallbacks
 * @param {React.Component} Component - The component to wrap
 * @returns {React.Component} - Wrapped component with i18n safety
 */
const withI18nSafety = (Component) => {
  return (props) => {
    return <Component {...props} />;
  };
};

/**
 * Utility function to safely get array/object translations with fallback
 * @param {Function} t - Translation function from useTranslation
 * @param {string} key - Translation key
 * @param {Array|Object} fallback - Fallback value if translation fails
 * @returns {Array|Object} - Translation value or fallback
 */
export const getSafeTranslation = (t, key, fallback = []) => {
  try {
    const result = t(key, { returnObjects: true });
    
    // Check if result is valid
    if (result === key || result === undefined || result === null) {
      console.warn(`Translation missing or invalid for key: ${key}`);
      return fallback;
    }
    
    // For arrays, check if it's actually an array
    if (Array.isArray(fallback) && !Array.isArray(result)) {
      console.warn(`Translation for ${key} is not an array, expected array`);
      return fallback;
    }
    
    // For objects, check if it's actually an object
    if (typeof fallback === 'object' && !Array.isArray(fallback) && typeof result !== 'object') {
      console.warn(`Translation for ${key} is not an object, expected object`);
      return fallback;
    }
    
    return result;
  } catch (error) {
    console.error(`Error getting translation for key: ${key}`, error);
    return fallback;
  }
};

/**
 * Safe wrapper for mapping over translated arrays
 * @param {Function} t - Translation function
 * @param {string} key - Translation key
 * @param {Function} mapFn - Map function to apply
 * @param {Array} fallback - Fallback array
 * @returns {Array} - Mapped array or empty array
 */
export const safeMapTranslation = (t, key, mapFn, fallback = []) => {
  const items = getSafeTranslation(t, key, fallback);
  
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }
  
  try {
    return items.map(mapFn);
  } catch (error) {
    console.error(`Error mapping translation for key: ${key}`, error);
    return [];
  }
};

export default withI18nSafety;
